'use client'

import { FC } from 'react'
import type { PortableTextBlock } from '@portabletext/types'
import styles from './NewsletterSignUpSection.module.scss'
import useI18n from '@/hooks/use-i18n'
import FormResult from '@/components/EmailSignupInput/FormResults/FormResults'
import RichText from '@/components/RichText/RichText'
import {
  headerSerializer,
  subtitleSerializer,
  successMessageTitleSerializer,
  successMessageSubtitleSerializer,
} from './serializers'
import { useNewsletterForm } from '@/hooks/useNewsletterForm'
import { NewsletterFormFields } from '@/components/NewsletterFormFields/NewsletterFormFields'
import { useNewsletterLocations } from '@/hooks/useNewsletterLocations'

type NewsletterFormProps = {
  successMessage?: {
    title?: PortableTextBlock[]
    subtitle?: PortableTextBlock[]
  }
}

const NewsletterForm: FC<NewsletterFormProps> = ({ successMessage }) => {
  const { i18n } = useI18n()

  const { title, subtitle } = successMessage || {}

  const { locations } = useNewsletterLocations()

  const {
    formData,
    errors,
    loading,
    formSubmitSuccess,
    formSubmit,
    memberAlreadySubscribed,
    submitContext,
    handleChange,
    handleDateChange,
    handleSubmit,
  } = useNewsletterForm()

  return (
    <>
      {formSubmitSuccess ? (
        <div
          className={styles.successMessageWrapper}
          data-gtm-visibility="success-message"
        >
          {memberAlreadySubscribed ? (
            <p>{i18n('alreadyBaoBaoInsider')}</p>
          ) : (
            <>
              {title && title.length > 0 ? (
                <RichText
                  value={title}
                  components={successMessageTitleSerializer}
                />
              ) : (
                <p>{i18n('thankYou')}</p>
              )}
              {subtitle && subtitle.length > 0 && (
                <RichText
                  value={subtitle}
                  components={successMessageSubtitleSerializer}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className={styles.form}
        >
          <NewsletterFormFields
            formData={formData}
            errors={errors}
            loading={loading}
            locations={locations}
            handleChange={handleChange}
            handleDateChange={handleDateChange}
            inputClassName={styles.input}
            selectClassName={styles.select}
            rowClassName={styles.row}
            formFieldClassName={styles.formField}
            submitButtonClassName={styles.submitButton}
            errorClassName={styles.error}
          />

          {formSubmit && !loading && (
            <div className={styles.FormResults}>
              {formSubmitSuccess ? (
                <FormResult
                  id="newsletter_signup_success"
                  status="success"
                  successMessage={
                    submitContext === 'already_subscribed' ? (
                      <p>{i18n('alreadyBaoBaoInsider')}</p>
                    ) : submitContext === 'updated' ? (
                      <p>{i18n('profileUpdated')}</p>
                    ) : submitContext === 'resubscribed' ? (
                      <p>{i18n('welcomeBackResubscribed')}</p>
                    ) : (
                      <>
                        {Array.isArray(title) && (title || []).length > 0 ? (
                          <RichText
                            value={title}
                            components={headerSerializer}
                          />
                        ) : (
                          <p>{i18n('thankYou')}</p>
                        )}
                        {Array.isArray(subtitle) && (subtitle || []).length > 0 && (
                          <RichText
                            value={subtitle}
                            components={subtitleSerializer}
                          />
                        )}
                      </>
                    )
                  }
                />
              ) : (
                <FormResult
                  status="error"
                  errorMessage={<p>{i18n('somethingWentWrong')}</p>}
                />
              )}
            </div>
          )}
        </form>
      )}
    </>
  )
}

export default NewsletterForm
